import API_BASE from '../config';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { setTrackedEvaluationId } from '../services/journeyTracking';
import { getTrackedUtm } from '../services/utmTracking';

const API_URL = API_BASE;

const extractErrorMessage = (detail, fallback = 'Something went wrong. Please try again.') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail.message) return detail.message;
  try { return JSON.stringify(detail); } catch { return fallback; }
};

const colors = {
  primary: '#1A6FD6',
  primaryLight: '#E8F1FC',
  navy: '#0D1F3C',
  dark: '#0D1F3C',
  body: '#6B7280',
  background: 'linear-gradient(135deg, #E8F1FC 0%, #F8FAFF 50%, #FFFFFF 100%)',
  surface: '#FFFFFF',
  border: '#E5E9F0'
};

// Countdown pacing. DISPLAY_SECONDS is what the user sees the counter
// count down from. TARGET_REAL_SECONDS is how long — in real wall-clock
// seconds — the counter should take to reach 0. When pipeline timing
// changes, retune by editing these two numbers only; the tick interval
// derives from them so nothing else needs to move.
const DISPLAY_SECONDS = 60;
const TARGET_REAL_SECONDS = 70;
const TICK_MS = 200;

// Hard ceiling on how long the wait screen will sit on "Almost there"
// before giving up. Backend has two 90s deadlines (extract + evaluate),
// so a healthy failure surfaces well before this. This catches the case
// where the backend hangs entirely — event loop wedged, network dropped,
// poll returning nothing — so the user is never stranded here.
const FRONTEND_CEILING_SECONDS = 210;

// The six-card story of what the pipeline is doing while the user waits.
// Each card is an array of paragraphs; the break points were chosen with
// the copy. This is the main visual element; the counter supports it.
const STORY_STEPS = [
  [
    "You applied. You waited. Nothing came back, not even a no.",
    "Here is what nobody tells you. Recruiters do not browse LinkedIn. We search it, on a screen you have never seen.",
  ],
  [
    "We open one opening and more than 800 profiles are sitting in it.",
    "Nobody reads 800. Not one of us ever has.",
  ],
  [
    "We type 3 or 4 keywords into that search box. 800 becomes 20.",
    "Your profile never made those 20. Nobody turned you down. Nobody opened you.",
  ],
  [
    "Those 20 get read line by line. 6 or 8 reach the hiring manager.",
    "That is the room where your work would have spoken for itself.",
  ],
  [
    "Most weeks we do not wait for anyone to apply. We go looking.",
    "It is not a better career that puts someone on top. It is a complete profile. LinkedIn ranks those higher, and those are the ones we message.",
  ],
  [
    "Recruiters do not search on emotion. Nobody types \"desperate\" or \"actively looking\" into the box. We search for the best person we can put in front of a hiring manager.",
    "Right now we are checking whether that box can find you.",
  ],
];

// Weight each card's share of TARGET_REAL_SECONDS by its total word count
// (both paragraphs summed) so longer beats read for longer without
// hardcoding a duration per card. Editing STORY_STEPS retunes the pacing
// automatically — no other change.
const STORY_WORD_COUNTS = STORY_STEPS.map(paras =>
  paras.reduce((n, p) => n + p.trim().split(/\s+/).length, 0),
);
const STORY_WORD_TOTAL = STORY_WORD_COUNTS.reduce((a, b) => a + b, 0);
const STORY_DURATIONS = STORY_WORD_COUNTS.map(
  w => TARGET_REAL_SECONDS * (w / STORY_WORD_TOTAL),
);
// Cumulative right-edge of each card on the timeline. activeStep is the
// smallest i whose boundary is still ahead of elapsedSec.
const STORY_BOUNDARIES = STORY_DURATIONS.reduce((acc, d) => {
  acc.push((acc[acc.length - 1] || 0) + d);
  return acc;
}, []);

const AnalysisLoading = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pdfStateRef = useRef(location.state || null); // { pdfFile, targetRole, targetCompany, whatsappNumber }
  // Single time source. `remaining`, the story stepper and the ring
  // progress all derive from this — no state drift between them.
  const [elapsedSec, setElapsedSec] = useState(0);
  // Failure surface. When fail() sets this, the wait UI is replaced by
  // an in-page card carrying the same message that used to go to alert().
  const [failureMessage, setFailureMessage] = useState('');
  // Backend's exact wording (backend/server.py, backend/routes/evaluation.py)
  // when is_recently_verified() rejects the eval call despite OTP having
  // just been verified moments earlier on this same screen — a real report
  // of this happening intermittently, cause not pinned down. Whatever the
  // trigger, forcing a full restart (re-upload PDF, re-enter everything) for
  // what's very likely just a fresh OTP away from working is a bad recovery
  // path. pdfStateRef/sessionRef are refs — untouched by resetting this
  // component's state — so re-verifying in place doesn't lose that data.
  const isOtpVerificationFailure = failureMessage.includes('verify your WhatsApp number');
  const doneRef = useRef(false); // prevent double-navigate

  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpInfo, setOtpInfo] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [editingNumber, setEditingNumber] = useState(false);

  // Holds the session data so startEval can read it without re-parsing sessionStorage
  const sessionRef = useRef({ analysisId: null, evaluationParamsRaw: null });

  const sendOtp = async (number) => {
    const target = number || phoneNumber;
    if (!target) return;
    setOtpSending(true);
    setOtpError('');
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: target }),
      });
      if (!res.ok) throw new Error('Could not send the code. Please check the number and try again.');
      setOtpSent(true);
    } catch (e) {
      setOtpError(e.message || 'Could not send the code. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!otpCode.trim()) return;
    setOtpVerifying(true);
    setOtpError('');
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber, otp: otpCode.trim() }),
      });
      if (!res.ok) throw new Error('That code is not correct. Please try again.');
      // /api/whatsapp/verify-otp always answers HTTP 200 — success/failure
      // (wrong code, expired, too many attempts) is encoded in the JSON body's
      // `success` field, not the HTTP status (confirmed correct elsewhere in
      // the app: WhatsAppVerifier.jsx checks data.success, not res.ok). This
      // function was checking res.ok only, so ANY wrong or expired code was
      // silently treated as verified — the OTP screen would advance straight
      // to the 60-second loading animation on a wrong code, running for the
      // full wait only to fail at the very end with a confusing, seemingly
      // unrelated "please verify your WhatsApp number" error — because the
      // number was, correctly, never actually verified.
      const verifyData = await res.json();
      if (!verifyData.success) {
        throw new Error(verifyData.message || 'That code is not correct. Please try again.');
      }

      setOtpVerified(true);

      // After OTP verify — check returning-user status before starting eval
      let returningStatus = null;
      try {
        const checkRes = await fetch(`${API_URL}/api/user/returning-check?whatsapp=${encodeURIComponent(phoneNumber)}`);
        if (checkRes.ok) {
          returningStatus = await checkRes.json();
        }
      } catch (e) {
        // Non-fatal — if check fails, fall through to normal eval
      }

      if (returningStatus) {
        const { status, prev_result, order_id } = returningStatus;

        if (status === 'quota_exhausted' && prev_result) {
          // Show their previous result directly — no new eval needed
          sessionStorage.setItem('analysisResult', JSON.stringify({
            status: 'completed',
            analysis_result: prev_result,
            userInput: JSON.parse(sessionStorage.getItem('userInput') || '{}'),
            returning: true,
          }));
          navigate('/results', { replace: true });
          return;
        }

        if (status === 'quota_exhausted' && !prev_result) {
          // Quota used, no stored result — send to upgrade
          navigate('/checkout', { replace: true });
          return;
        }

        if (status === 'subscribed_pending') {
          setOtpInfo('Your rewrite is being prepared — delivered to your WhatsApp within 48 hours.');
          setOtpVerifying(false);
          return;
        }

        if (status === 'subscribed_delivered') {
          setOtpInfo('Your rewrite is ready — check your WhatsApp for the link.');
          setOtpVerifying(false);
          return;
        }

        // status === 'new' — fall through to normal eval
      }
    } catch (e) {
      setOtpError(e.message || 'That code is not correct. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  // Recovery path for the OTP-verification failure specifically (see
  // isOtpVerificationFailure above) — sends a fresh code and drops back to
  // the OTP-entry screen in place, instead of forcing a full restart via
  // "Return to the form". pdfStateRef/sessionRef (the PDF file, target role,
  // etc.) are refs, so they're untouched by this.
  const retryVerification = () => {
    doneRef.current = false;
    setFailureMessage('');
    setOtpVerified(false);
    setOtpCode('');
    setOtpSent(false);
    sendOtp(phoneNumber);
  };

  // Mount: read session data (or Router state for PDF flow), extract phone number, auto-send OTP.
  // Does NOT start the countdown, eval, or poll — those fire in startEval() after OTP verify.
  useEffect(() => {
    document.title = 'Evaluating your profile — Successlane';

    const analysisId = sessionStorage.getItem('analysisId');
    const evaluationParamsRaw = sessionStorage.getItem('evaluationParams');
    const hasPdfState = !!(pdfStateRef.current?.pdfFile);

    if (!analysisId && !evaluationParamsRaw && !hasPdfState) {
      navigate('/analyze', { replace: true });
      return;
    }

    sessionRef.current = { analysisId, evaluationParamsRaw };

    // Extract WhatsApp number — URL flow: evaluationParams.whatsapp_number,
    // PDF flow: Router state.whatsappNumber (set in submitPdfFlow).
    let phone = '';
    if (evaluationParamsRaw) {
      const params = JSON.parse(evaluationParamsRaw);
      phone = params.whatsapp_number || '';
    }
    if (!phone && hasPdfState) {
      phone = pdfStateRef.current.whatsappNumber || '';
    }
    if (!phone) {
      const userInputRaw = sessionStorage.getItem('userInput');
      if (userInputRaw) {
        const ui = JSON.parse(userInputRaw);
        phone = ui.whatsappNumber || '';
      }
    }
    setPhoneNumber(phone);
    sendOtp(phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Fires the eval / starts polling — only called once OTP is verified.
  const startEval = async () => {
    const startedAt = Date.now();
    const tickInterval = setInterval(() => {
      setElapsedSec((Date.now() - startedAt) / 1000);
    }, TICK_MS);

    // Frontend safety net. If neither finish() nor fail() has been
    // called by FRONTEND_CEILING_SECONDS, treat the eval as hung and
    // bounce the user out of the loading screen. doneRef guards make
    // this a no-op if the eval resolved first.
    const ceilingTimeout = setTimeout(() => {
      fail('This is taking longer than expected. Please try again in a moment.');
    }, FRONTEND_CEILING_SECONDS * 1000);

    const finish = (result) => {
      if (doneRef.current) return;
      doneRef.current = true;
      clearInterval(tickInterval);
      clearTimeout(ceilingTimeout);
      sessionStorage.setItem('analysisResult', JSON.stringify(result));
      sessionStorage.removeItem('analysisId');
      sessionStorage.removeItem('evaluationParams');
      navigate('/results', { replace: true });
    };

    const fail = (msg) => {
      if (doneRef.current) return;
      doneRef.current = true;
      clearInterval(tickInterval);
      clearTimeout(ceilingTimeout);
      sessionStorage.removeItem('analysisId');
      sessionStorage.removeItem('evaluationParams');
      // In-page failure card. Same message that used to go to alert().
      // Navigation is deferred until the user clicks the button — no more
      // ambush after a three-minute wait.
      setFailureMessage(msg || 'Analysis failed. Please try again.');
    };

    // ── PDF flow: upload now (post-OTP) and get analysisId ──
    if (pdfStateRef.current?.pdfFile) {
      try {
        const fd = new FormData();
        fd.append('file', pdfStateRef.current.pdfFile);
        fd.append('targetRole', pdfStateRef.current.targetRole || '');
        fd.append('targetCompany', pdfStateRef.current.targetCompany || '');
        fd.append('whatsappNumber', pdfStateRef.current.whatsappNumber || '');
        const res = await fetch(`${API_URL}/api/analyze-profile`, {
          method: 'POST',
          body: fd,
        });
        if (res.status === 402) {
          const data = await res.json().catch(() => ({}));
          sessionStorage.setItem('quotaExhausted', JSON.stringify(data?.detail || {}));
          // Silent redirect — InputForm reads sessionStorage on mount and
          // shows the pricing popup. No alert() because the popup carries
          // the full message + CTA.
          doneRef.current = true;
          clearInterval(tickInterval);
          clearTimeout(ceilingTimeout);
          sessionStorage.removeItem('analysisId');
          sessionStorage.removeItem('evaluationParams');
          navigate('/analyze', { replace: true });
          return;
        }
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          fail(extractErrorMessage(data?.detail, 'Upload failed. Please try again.'));
          return;
        }
        const data = await res.json();
        // Store analysisId so the poll loop below can find it
        sessionRef.current = { ...sessionRef.current, analysisId: data.analysis_id };
        if (data?.quota) {
          sessionStorage.setItem('lastQuota', JSON.stringify(data.quota));
        }
      } catch (e) {
        fail(extractErrorMessage(e?.detail || e?.message, 'Upload failed. Please try again.'));
        return;
      }
    }

    const { analysisId, evaluationParamsRaw } = sessionRef.current;

    // ── URL flow: kick off async evaluate then poll analysis row ──
    if (evaluationParamsRaw) {
      const params = JSON.parse(evaluationParamsRaw);
      let urlAnalysisId = null;
      try {
        const utm = getTrackedUtm();
        const res = await fetch(`${API_URL}/api/evaluation/evaluate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            profile_data: params.profile_data,
            target_role: params.target_role,
            target_company: params.target_company || null,
            target_industry: params.target_company || null,
            linkedin_url: params.linkedin_url || null,
            input_method: 'url',
            whatsapp_number: params.whatsapp_number,
            utm_source: utm.source,
            utm_campaign: utm.campaign,
            utm_term: utm.term,
          })
        });
        const data = await res.json();
        if (!res.ok) {
          if (res.status === 402) {
            sessionStorage.setItem('quotaExhausted', JSON.stringify(data?.detail || {}));
            doneRef.current = true;
            clearInterval(tickInterval);
            clearTimeout(ceilingTimeout);
            sessionStorage.removeItem('analysisId');
            sessionStorage.removeItem('evaluationParams');
            navigate('/analyze', { replace: true });
            return;
          }
          fail(extractErrorMessage(data?.detail, 'Evaluation failed. Please try again.'));
          return;
        }
        if (data?.quota) {
          sessionStorage.setItem('lastQuota', JSON.stringify(data.quota));
        }
        urlAnalysisId = data.analysis_id || data.evaluation_id;
        if (!urlAnalysisId) {
          fail('Evaluation failed. Please try again.');
          return;
        }
        setTrackedEvaluationId(urlAnalysisId);
      } catch (err) {
        // Logged, not just swallowed: this exact catch is what turned a
        // real bug (a ReferenceError in the 402-handling branch above,
        // fixed alongside this) into a misleading generic "Network error"
        // for every affected user, with nothing in the console pointing
        // at the actual cause. Whatever throws here now, the real error
        // is at least visible in devtools instead of disappearing.
        console.error('[AnalysisLoading] evaluate request failed:', err);
        fail('Network error. Please try again.');
        return;
      }

      // Poll the shared /api/analysis/{id} endpoint until the background
      // task on the server flips status → completed / failed. Mirrors the
      // PDF flow below on purpose — the async URL flow uses the exact same
      // profile_analyses row so a single poller can handle both.
      const urlPollInterval = setInterval(async () => {
        try {
          const result = await apiService.getAnalysis(urlAnalysisId);
          if (result.status === 'completed') {
            clearInterval(urlPollInterval);
            finish({ status: 'completed', analysis_result: result.analysis_result });
          } else if (result.status === 'failed') {
            clearInterval(urlPollInterval);
            fail(result.error_message || 'Evaluation failed. Please try again.');
          }
        } catch {
          // non-fatal polling error — keep trying
        }
      }, 2000);

      return () => {
        clearInterval(tickInterval);
        clearInterval(urlPollInterval);
        clearTimeout(ceilingTimeout);
      };
    }

    // ── PDF flow: poll profile_analyses table ──
    const pollInterval = setInterval(async () => {
      try {
        const result = await apiService.getAnalysis(analysisId);
        if (result.status === 'completed') {
          // PDF route: analysis_id doubles as the profile_evaluations id
          // (server.py mirrors the row using it as the primary key).
          setTrackedEvaluationId(analysisId);
          finish({ status: 'completed', analysis_result: result.analysis_result });
        } else if (result.status === 'failed') {
          // Backend writes plain-English detail into error_message on the
          // profile_analyses row when process_analysis raises. Forward it
          // so PDF and URL flows show the same text for the same failure.
          fail(result.error_message || 'Analysis failed. Please try again.');
        }
      } catch {
        // non-fatal polling error — keep trying
      }
    }, 2000);

    // Cleanup only needed for the PDF poll; URL fetch is fire-and-forget
    return () => {
      clearInterval(tickInterval);
      clearInterval(pollInterval);
      clearTimeout(ceilingTimeout);
    };
  };

  // Trigger the eval once OTP is verified
  useEffect(() => {
    if (otpVerified) {
      startEval();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpVerified]);

  // Counter reaches 0 at exactly TARGET_REAL_SECONDS; past that,
  // `exhausted` flips to true and the counter is replaced with the
  // "Almost there" message. No floor, no negative values, no freeze.
  const remaining = Math.max(0, DISPLAY_SECONDS * (1 - elapsedSec / TARGET_REAL_SECONDS));
  const exhausted = elapsedSec >= TARGET_REAL_SECONDS;
  const progress = Math.min(100, (elapsedSec / TARGET_REAL_SECONDS) * 100);
  // First card whose boundary is still ahead of the elapsed clock. Once
  // the counter exhausts we hold on the last card — the counter's
  // "Almost there" line becomes the handover, not a seventh story beat.
  const activeStep = exhausted
    ? STORY_STEPS.length - 1
    : (STORY_BOUNDARIES.findIndex(b => elapsedSec < b) !== -1
        ? STORY_BOUNDARIES.findIndex(b => elapsedSec < b)
        : STORY_STEPS.length - 1);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ background: colors.background, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
      `}</style>

      <div className="max-w-[480px] w-full text-center">
        <div
          className="mb-8 flex items-center justify-center"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <img src="/images/brand/successlane-logo-mark.png" alt="Successlane" style={{ height: 28, width: 'auto', display: 'block' }} />
        </div>

        {failureMessage ? (
          <div className="max-w-md mx-auto text-center px-4" data-testid="failure-card">
            <div
              className="rounded-xl border px-6 py-8 mb-6"
              style={{ borderColor: colors.border, backgroundColor: colors.surface }}
            >
              <p
                className="text-base"
                style={{ color: colors.dark, lineHeight: 1.55 }}
                data-testid="failure-message"
              >
                {failureMessage}
              </p>
            </div>
            {isOtpVerificationFailure ? (
              <>
                <button
                  onClick={retryVerification}
                  className="w-full py-3 rounded-lg font-semibold"
                  style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
                  data-testid="failure-button"
                >
                  Send a new code and try again
                </button>
                <button
                  onClick={() => navigate('/analyze', { replace: true })}
                  className="w-full py-3 mt-3 text-sm font-medium underline"
                  style={{ color: colors.body, background: 'none', border: 'none' }}
                >
                  Or start over from the form
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/analyze', { replace: true })}
                className="w-full py-3 rounded-lg font-semibold"
                style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}
                data-testid="failure-button"
              >
                Return to the form
              </button>
            )}
          </div>
        ) : !otpVerified ? (
          <div className="max-w-md mx-auto text-center px-4">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Your evaluation is ready</h2>
            <p className="text-slate-500 mb-6">
              Enter the code we sent to your WhatsApp to start it.
            </p>

            {!editingNumber ? (
              <p className="text-sm text-slate-600 mb-4">
                Code sent to <span className="font-medium">{phoneNumber}</span>{' '}
                <button onClick={() => setEditingNumber(true)} className="text-blue-600 underline ml-1">Edit</button>
              </p>
            ) : (
              <div className="mb-4 flex gap-2 justify-center">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg"
                  placeholder="WhatsApp number"
                />
                <button
                  onClick={() => { setEditingNumber(false); sendOtp(phoneNumber); }}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm"
                >Resend</button>
              </div>
            )}

            <input
              type="text"
              inputMode="numeric"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              placeholder="Enter code"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-lg tracking-widest mb-3"
            />

            {otpError && <p className="text-sm text-red-500 mb-3">{otpError}</p>}
            {otpInfo && <p className="text-sm text-blue-600 mb-3">{otpInfo}</p>}

            <button
              onClick={verifyOtp}
              disabled={otpVerifying || !otpCode.trim()}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold disabled:opacity-50"
            >
              {otpVerifying ? 'Verifying…' : 'Verify & start evaluation'}
            </button>

            <div className="mt-3 flex justify-center gap-4 text-sm">
              <button onClick={() => sendOtp(phoneNumber)} disabled={otpSending} className="text-blue-600 underline disabled:opacity-50">
                {otpSending ? 'Sending…' : 'Resend code'}
              </button>
            </div>

            <p className="mt-6 text-xs text-slate-500 flex items-center justify-center gap-1">
              <span>🔒</span>
              We use your number only to send your results and verify it's you. No spam, no calls, no sharing — ever.
            </p>
          </div>
        ) : (
          <>
            {/* Persistent framer above the counter. Not part of the story
                fade — this stays visible throughout the wait. */}
            <p
              className="mb-6 text-sm text-center"
              style={{ color: colors.body, lineHeight: 1.55 }}
              data-testid="wait-static-header"
            >
              This takes under a minute. Here is what happens on the other side while you wait.
            </p>

            {/* Counter — enlarged so the number reads at a glance without
                becoming the hero. Same slot swaps to the "Almost there"
                line once the counter exhausts. */}
            <div className="mb-8 h-24 flex items-center justify-center" data-testid="wait-counter">
              {!exhausted ? (
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="48" cy="48" r="42" fill="none" stroke={colors.border} strokeWidth="6" />
                    <circle
                      cx="48" cy="48" r="42" fill="none" stroke={colors.primary} strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={`${(progress * 263.894) / 100} 263.894`}
                      style={{ transition: `stroke-dasharray ${TICK_MS}ms linear` }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span
                      className="text-3xl font-bold"
                      style={{ color: colors.dark, fontFamily: 'Fraunces, serif', lineHeight: 1 }}
                    >
                      {Math.floor(remaining)}
                    </span>
                    <span
                      className="uppercase tracking-wider mt-1"
                      style={{ color: colors.body, fontSize: 10 }}
                    >
                      seconds
                    </span>
                  </div>
                </div>
              ) : (
                <p
                  className="text-base text-center px-4"
                  style={{ color: colors.body }}
                  data-testid="wait-almost-there"
                >
                  Almost there. Putting your report together.
                </p>
              )}
            </div>

            {/* One card at a time, cross-fading. All six share one grid cell
                (gridColumn/Row 1) so the container sizes to the *tallest*
                card and each individual card sizes to its own copy — no
                fixed height, no empty box on short beats. Padding is
                generous. Cards break into two paragraphs at the sentence
                boundaries chosen with the copy. */}
            <div
              className="mb-6 grid"
              style={{ gridTemplateColumns: '1fr' }}
              data-testid="wait-story"
            >
              {STORY_STEPS.map((paragraphs, i) => (
                <div
                  key={i}
                  className="rounded-xl border"
                  style={{
                    gridColumn: 1,
                    gridRow: 1,
                    padding: '28px 24px',
                    opacity: i === activeStep ? 1 : 0,
                    transition: 'opacity 600ms ease-in-out',
                    borderColor: colors.primary,
                    backgroundColor: colors.primaryLight,
                    pointerEvents: i === activeStep ? 'auto' : 'none',
                  }}
                  aria-hidden={i !== activeStep}
                >
                  {paragraphs.map((para, pi) => (
                    <p
                      key={pi}
                      className="text-left"
                      style={{
                        color: colors.dark,
                        fontSize: '1.0625rem',
                        lineHeight: 1.55,
                        marginTop: pi > 0 ? '0.9rem' : 0,
                      }}
                    >
                      {para}
                    </p>
                  ))}
                </div>
              ))}
            </div>

            {/* Position indicator — where you are in the story, not what's
                been ticked off. Current one is a filled pill; the other
                five stay as flat dots. No completion state. Holds on the
                sixth card past exhaustion — never loops. */}
            <div
              className="flex items-center justify-center gap-1.5"
              data-testid="wait-position"
              aria-label={`Step ${activeStep + 1} of ${STORY_STEPS.length}`}
            >
              {STORY_STEPS.map((_, i) => (
                <span
                  key={i}
                  className="rounded-full"
                  style={{
                    width: i === activeStep ? 20 : 6,
                    height: 6,
                    backgroundColor: i === activeStep ? colors.primary : colors.border,
                    transition: 'width 300ms ease, background-color 300ms ease',
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalysisLoading;
